// Layer 4 (UI) — draws the one-throughput-axis figure. Every mark, label and
// position comes from `capacityAxisView`; this file maps tones to tokens and
// measures its own width so text renders at true pixel size.

import { useLayoutEffect, useRef, useState } from 'react';
import type { CapacityRead } from '@/versions/v1/schema/capacity';
import type { BottleneckLadderProjection } from './BottleneckLadder.transform';
import { capacityAxisView, type AxisTone } from './CapacityAxis.transform';
import type { SelectableScenarioId } from './ThroughputTab.transform';

const TONE: Record<AxisTone, string> = {
  ink: 'var(--ink-primary)',
  ink2: 'var(--ink-secondary)',
  ink3: 'var(--ink-tertiary)',
  ok: 'var(--state-online)',
  warn: 'var(--state-warning)',
  bad: 'var(--state-blocked)',
  info: 'var(--state-info)',
  accent: 'var(--color-accent-500)',
  surface: 'var(--surface-2)',
};

export function CapacityAxis({
  read,
  ladder,
  selectedScenarioId,
}: {
  read: CapacityRead;
  ladder: BottleneckLadderProjection;
  selectedScenarioId: SelectableScenarioId;
}) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(800);
  useLayoutEffect(() => {
    const element = wrapperRef.current;
    if (!element) return;
    const measure = () => setWidth(Math.round(element.getBoundingClientRect().width));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const view = capacityAxisView(read, ladder, selectedScenarioId, width);

  return (
    <div ref={wrapperRef} className="min-w-0">
      <svg
        viewBox={`0 0 ${view.width} ${view.height}`}
        role="img"
        aria-label={view.ariaLabel}
        className="block h-auto w-full tabular-nums"
      >
        {view.lines.filter((line) => line.opacity !== undefined).map((line, index) => (
          <line key={`grid-${index}`} x1={line.x1} x2={line.x2} y1={line.y1} y2={line.y2} stroke={TONE[line.tone]} strokeWidth={line.width} strokeOpacity={line.opacity} strokeDasharray={line.dash} />
        ))}
        {view.rects.map((rect, index) => (
          <rect key={`lane-${index}`} x={rect.x} y={rect.y} width={rect.width} height={rect.height} rx={3} fill={TONE[rect.tone]} fillOpacity={rect.opacity} />
        ))}
        {view.paths.map((path, index) => (
          <path key={`path-${index}`} d={path.d} fill="none" stroke={TONE[path.tone]} strokeWidth={path.width} />
        ))}
        {view.lines.filter((line) => line.opacity === undefined).map((line, index) => (
          <line key={`mark-${index}`} x1={line.x1} x2={line.x2} y1={line.y1} y2={line.y2} stroke={TONE[line.tone]} strokeWidth={line.width} strokeDasharray={line.dash} />
        ))}
        {view.circles.map((circle, index) => (
          <circle key={`node-${index}`} cx={circle.cx} cy={circle.cy} r={circle.r} fill={TONE[circle.fill]} stroke={TONE[circle.stroke]} strokeWidth={circle.strokeWidth}>
            <title>{circle.title}</title>
          </circle>
        ))}
        {view.texts.map((text, index) => (
          <text
            key={`text-${index}`}
            x={text.x}
            y={text.y}
            textAnchor={text.anchor}
            fontSize={text.size}
            fontWeight={text.weight}
            fill={TONE[text.tone]}
            letterSpacing={text.caps ? '0.07em' : undefined}
            className={text.caps ? 'uppercase' : undefined}
            paintOrder="stroke"
            stroke="var(--surface-2)"
            strokeWidth={text.caps ? 0 : 3}
            strokeLinejoin="round"
          >
            {text.spans.map((span, spanIndex) => (
              <tspan key={spanIndex} fill={TONE[span.tone]} fontWeight={span.weight} fontSize={span.size}>{span.text}</tspan>
            ))}
          </text>
        ))}
      </svg>
    </div>
  );
}
